import {
  BaseSchema,
  buildSchemaAtPath,
  createTree,
  decorateTreeWithPaths,
  getObjectAtPath,
  getSchemaNodeAtPath,
  SchemaNode,
} from "./node";
import { DesignTransformer, Transform } from "./design";
import {
  DisplayAdapter,
  Render,
  RenderToMap,
  renderWithoutEnvelope,
} from "./renderer";
import { Filter } from "./filter";

interface EnumValues {
  [key: string]: string;
}

interface EnumDefinition {
  paths: string[];
  values: EnumValues;
}

type AnyObject = Record<string, unknown>;

// This is used when you just want to query for individual schema nodes with overrides
// applied, but don't want any trimming of the schema due to design constraints or pre-populated data.
export class SchemaRepository {
  schemas: AnyObject;
  overrides?: AnyObject;
  schemaGraph: SchemaNode;

  constructor(schemas: AnyObject, overrides?: AnyObject) {
    this.schemas = schemas;
    this.overrides = overrides;

    let graph = createTree(schemas);
    if (graph === null) {
      throw new Error("Could not create graph from schema file");
    }
    graph = decorateTreeWithPaths(graph);
    this.schemaGraph = graph;
    if (!(overrides)) {
      return;
    }

    let overrideGraph = createTree(overrides);
    if (overrideGraph === null) {
      throw new Error("Could not create graph from schema file");
    }
    overrideGraph = decorateTreeWithPaths(overrideGraph);
    this.schemaGraph = this.schemaGraph.combine(overrideGraph);
  }

  getSchemaAtPath(path: string): (SchemaNode | null) {
    return getSchemaNodeAtPath(this.schemaGraph, path);
  }
}

// This class is responsible for one thing: taking in a schema and enum file, and given a set of required segments/fields,
// (including segments/fields from a pre-populated data set)
// returns just that specific subsection from the EDI document.
export class SchemaBuilder {
  schemaFile: AnyObject;
  schemaGraph: SchemaNode;
  enumsFile: EnumDefinition[];

  constructor(
    schemaFile: Record<string, unknown>,
    enumsFile: EnumDefinition[],
  ) {
    this.schemaFile = schemaFile;
    this.enumsFile = enumsFile;
    let graph = createTree(schemaFile);
    if (graph === null) {
      throw new Error("Could not create graph from schema file");
    }
    graph = decorateTreeWithPaths(graph);
    this.schemaGraph = graph;
  }

  // combineSpecs will combine n number of schemas into a single schema. If there are conflicts, the last schema will win.
  private combineSpecs(...schemas: SchemaNode[]): SchemaNode | null {
    // detect if schema length is 0, if so, return null
    if (schemas == null || schemas.length === 0) {
      return null;
    }
    // for every schema, merge it into the result
    // if there are conflicts, handle them
    let currentSchema: SchemaNode = schemas[0];
    // if schema length is 1, return the schema
    if (schemas.length === 1) {
      return currentSchema;
    }
    // for every schema after the first, merge it into the current schema
    for (let i = 1; i < schemas.length; i++) {
      currentSchema = currentSchema.combine(schemas[i]);
    }
    return currentSchema;
  }

  public combineSpecsWithOverrides(schema: SchemaNode, overrides: AnyObject): SchemaNode {
    let graph = createTree(overrides);
    if (graph === null) {
      throw new Error("Could not create graph from schema file");
    }
    graph = decorateTreeWithPaths(graph);
    return schema.combineExistingOnly(graph);
  }

  // getSchemaFromData expects the root of the data object, and will handle walking the data object to find the schema
  public getSchemaFromData(
    sourceSchema: SchemaNode,
    currentSchema: SchemaNode,
    data: AnyObject,
  ): SchemaNode | null {
    // for every root key in the data, see if it matches a root key in the schema
    // if it does, create that schema object in the result
    // for every child of that root key, see if it matches a child in the schema
    // if it does, add it to the result
    // walk the branches and perform the same all the way down

    const paths = generatePaths(data, "");

    // HACK: the paths should now be prefixed with "envelope.heading" if they are not already
    for (let i = 0; i < paths.length; i++) {
      paths[i] = `envelope.heading.${paths[i]}`;
    }

    const renderedSchema = this.getSchemaFromRequirements(
      sourceSchema,
      currentSchema,
      paths,
    );
    if (renderedSchema === null) {
      throw new Error("Could not create schema from data");
    }

    // schema node with data added
    // now hydrate data into the schema
    return this.hydrateDataIntoSchema(paths, renderedSchema, data);
  }

  hydrateDataIntoSchema(
    paths: string[],
    schema: SchemaNode,
    data: AnyObject,
  ): SchemaNode {
    const output = schema.clone();
    // walk the data, and for every leaf, find the corresponding leaf in the schema
    // and set the value
    for (const key in paths) {
      const path = paths[key];
      // get the target in the schema
      const target = getSchemaNodeAtPath(output, path, true);
      if (target === null) {
        throw new Error(`Could not find target path: ${path}`);
      }
      // use the target path as the key to the data, since the resulting target *could* be an array, which would mean we
      // only need to find the objectData up to the array
      let objKey = target.getCurrentPath();
      // strip envelope and then heading from the path
      objKey = objKey.replace("envelope.", "");
      objKey = objKey.replace("heading.", "");
      // get the value of the data
      const dataValue = getObjectAtPath(data, objKey);
      if (dataValue === null) {
        throw new Error(`Could not find data path: ${objKey}`);
      }
      // set the value in the schema
      target.value = dataValue;
    }
    return output;
  }

  public getSchemaFromRequirements(
    sourceSchema: SchemaNode,
    currentSchema: SchemaNode,
    requirements: string[],
  ): SchemaNode | null {
    for (const requirement of requirements) {
      if (requirement === "") {
        continue;
      }
      let potentialSchema = buildSchemaAtPath(
        sourceSchema,
        currentSchema,
        requirement,
      );
      if (potentialSchema === null) {
        throw new Error(`Could not build schema from path: ${requirement}`);
      }
      potentialSchema = potentialSchema.root();
      // time the combine operation
      currentSchema = currentSchema.combine(potentialSchema).root();
      // the combined output needs to replace the current schema parent/children relationships
      if (currentSchema === null) {
        throw new Error("Could not combine schemas");
      }
    }
    return currentSchema.root();
  }

  public getFullTenderSchema(): SchemaNode | undefined {
    return this.schemaGraph;
  }

  // requirements must be in the form of a full path to the field, e.g. "general_shipment_information_BX.transaction_set_purpose_code_01"
  // this *could* support specifying a path to a segment, which would infer all fields in that segment are required
  // This does not return a full json-schema, but instead returns the collection of segments that belong to the heading/details/etc sections
  public composeSchema(
    populatedData: Record<string, unknown>,
    requirements: string[],
    overrideSchema?: Record<string, unknown>,
    ignoreList?: string[]
  ): SchemaNode {
    const baseSchemaGraph = createTree(BaseSchema);
    if (baseSchemaGraph === null) {
      throw new Error("Could not create graph from base schema");
    }
    // first, given the data, see if we can find the related section in the edi schema
    const dataSchema = this.getSchemaFromData(
      this.schemaGraph,
      baseSchemaGraph,
      populatedData,
    );
    if (dataSchema === null) {
      throw new Error("Could not create schema from data");
    }

    const requiredSchema = this.getSchemaFromRequirements(
      this.schemaGraph,
      baseSchemaGraph,
      requirements,
    );
    if (requiredSchema === null) {
      throw new Error("Could not create schema from requirements");
    }

    const schemas: SchemaNode[] = [dataSchema, requiredSchema];
    // then, for each requirement, add that as well
    let combinedSpec = this.combineSpecs(...schemas);
    if (combinedSpec === null) {
      throw new Error("Could not combine schemas");
    }
    // create the override schema
    if (overrideSchema) {
      const overrideSchemaGraph = createTree(overrideSchema);
      if (overrideSchemaGraph !== null) {
        combinedSpec = combinedSpec.combineExistingOnly(overrideSchemaGraph);
      }
    }

    // now filter out the ignore list
    if (ignoreList) {
      combinedSpec = Filter(combinedSpec, ignoreList);
    }
    return decorateTreeWithPaths(combinedSpec).root();
  }

  // transform changes the schema layout, while decorating the fields as needed
  public transform(
    input: SchemaNode,
    transformers: DesignTransformer[],
  ): SchemaNode {
    return Transform(input, transformers);
  }

  // render takes in the schema and returns display output, as the type T
  public render<T>(target: SchemaNode, adapters: DisplayAdapter<T>[]): T[] {
    return Render<T>(target, adapters);
  }

  // renderWithoutEnvelope returns the display output, as the type T, without the envelope
  public renderWithoutEnvelope<T>(
    target: SchemaNode,
    adapters: DisplayAdapter<T>[],
  ): T[] {
    return renderWithoutEnvelope(target, adapters);
  }

  // renderToMap returns the names of each section as a key in a dictionary, with the value being the output of the adapter
  public renderToMap<T>(
    target: SchemaNode,
    adapters: DisplayAdapter<T>[],
  ): Record<string, T[]> {
    return RenderToMap<T>(target, adapters);
  }
}

// in the case where data is the envelope, we want to eat it automatically even if it's not prescribed in the path
// in the case where the data is the heading, we  want to also eat it automatically, even if not prescribed in the path
// if heading is prescribed in path, we want to eat it
// then recursively iterate over the data object, and for each key, and generate path
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function generatePaths(data: any, currentPath: string = ""): string[] {
  let paths: string[] = [];
  // TODO: prefix "heading" to the path if it doesn't exist

  // if data.label == "envelope", then walk to data.properties
  if (data.label === "envelope") {
    data = data.properties;
  }

  // if data.label == "heading", then walk to data.properties
  if (data.label === "heading") {
    data = data.properties;
  }

  for (const key in data) {
    const newPath = currentPath ? `${currentPath}.${key}` : key;
    if (Array.isArray(data[key])) {
      const arrayItems = data[key];
      for (let i = 0; i < arrayItems.length; i++) {
        const target = arrayItems[i];
        paths = paths.concat(generatePaths(target, newPath ? newPath : key));
      }
      continue;
    }
    if (typeof data[key] === "object" && data[key] !== null) {
      paths = paths.concat(generatePaths(data[key], newPath));
    } else {
      paths.push(newPath);
    }
  }

  return paths;
}
